import { useEffect, useRef, useState, type FormEvent } from 'react'
import { usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import {
  NEWSLETTER_MODAL_COPY,
  type NewsletterTrigger,
  cleanupLegacySessionKey,
  fireNewsletterModalGtag,
  isSuppressedPath,
  readModalStorage,
  recordDismissed,
  recordSubmitted,
  shouldArmModal,
  variantForComponent,
} from '@/lib/newsletterModal'
import { useTurnstile } from '@/lib/turnstile'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

const RETRY_DELAY_MS = 1000
const SUCCESS_CLOSE_DELAY_MS = 2000
const SCROLL_ARM_GRACE_MS = 500
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const BLOCKING_OVERLAY_SELECTOR = [
  '[data-slot="dialog-content"][data-state="open"]',
  '[data-slot="sheet-content"][data-state="open"]',
  '[data-vaul-drawer][data-state="open"]',
].join(', ')

function hasBlockingOverlay() {
  return document.querySelector(BLOCKING_OVERLAY_SELECTOR) !== null
}

export default function NewsletterModal() {
  const {
    current_user: currentUser,
    current_organization: currentOrganization,
    settings,
    turnstile_site_key: turnstileSiteKey,
  } = usePage<SharedProps>().props
  const { url, component } = usePage()
  const enabled = settings.newsletter_modal_enabled
  const variant = variantForComponent(component)
  const copy = NEWSLETTER_MODAL_COPY[variant]

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const {
    containerRef: turnstileRef,
    getToken: getTurnstileToken,
    reset: resetTurnstile,
  } = useTurnstile(turnstileSiteKey, open)
  const triggerRef = useRef<NewsletterTrigger | null>(null)
  const retryTimerRef = useRef<number | null>(null)
  const successCloseTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (open) return

    const storage = readModalStorage()
    if (storage === null) return

    const loggedIn = Boolean(currentUser || currentOrganization)
    const armed = shouldArmModal({
      enabled,
      loggedIn,
      url,
      submitted: storage.submitted,
      dismissedAtMs: storage.dismissedAtMs,
      nowMs: Date.now(),
    })
    if (!armed) return

    cleanupLegacySessionKey()

    let fired = false
    const armedAt = performance.now()

    const openWhenClear = () => {
      if (hasBlockingOverlay()) {
        retryTimerRef.current = window.setTimeout(openWhenClear, RETRY_DELAY_MS)
        return
      }
      retryTimerRef.current = null
      setOpen(true)
      fireNewsletterModalGtag('subscribe_modal_shown', {
        variant,
        page_type: component,
        trigger: triggerRef.current ?? 'exit_intent',
      })
    }

    const fire = (trigger: NewsletterTrigger) => {
      if (fired) return
      fired = true
      removeListeners()
      triggerRef.current = trigger
      openWhenClear()
    }

    const onMouseOut = (e: MouseEvent) => {
      if (e.relatedTarget === null && e.clientY <= 0) fire('exit_intent')
    }

    const onScroll = () => {
      // Ignore the scroll event browsers fire when restoring a deep scroll
      // position on back-navigation; it is not a user gesture.
      if (performance.now() - armedAt < SCROLL_ARM_GRACE_MS) return
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if (window.scrollY / scrollable >= 0.5) fire('scroll_depth')
    }

    const removeListeners = () => {
      document.removeEventListener('mouseout', onMouseOut)
      window.removeEventListener('scroll', onScroll)
    }

    document.addEventListener('mouseout', onMouseOut)
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      removeListeners()
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [enabled, currentUser, currentOrganization, url, open, component, variant])

  useEffect(() => {
    if (currentUser || currentOrganization || isSuppressedPath(url)) {
      setOpen(false)
    }
  }, [currentUser, currentOrganization, url])

  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current)
      }
      if (successCloseTimerRef.current !== null) {
        window.clearTimeout(successCloseTimerRef.current)
      }
    }
  }, [])

  if (!enabled) return null
  if (currentUser || currentOrganization) return null
  if (isSuppressedPath(url)) return null

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen && status !== 'success') {
      recordDismissed()
      fireNewsletterModalGtag('subscribe_modal_dismissed', {
        variant,
        page_type: component,
        trigger: triggerRef.current ?? 'exit_intent',
      })
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()

    const trimmedEmail = email.trim()
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    setEmailError(null)
    setStatus('loading')

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      const turnstileToken = await getTurnstileToken()
      const res = await fetch('/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          email: trimmedEmail,
          variant,
          website,
          turnstile_token: turnstileToken,
        }),
      })

      if (res.ok) {
        setStatus('success')
        recordSubmitted()
        fireNewsletterModalGtag('subscribe_modal_submitted', {
          variant,
          page_type: component,
          trigger: triggerRef.current ?? 'exit_intent',
        })
        successCloseTimerRef.current = window.setTimeout(() => {
          setOpen(false)
        }, SUCCESS_CLOSE_DELAY_MS)
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    } finally {
      resetTurnstile()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md overflow-hidden p-0 gap-0">
        <div className="p-8">
          {status === 'success' ? (
            <div className="flex flex-col justify-center">
              <DialogTitle className="font-serif text-2xl font-semibold tracking-tight">
                {NEWSLETTER_MODAL_COPY.success.title}
              </DialogTitle>
              <DialogDescription className="mt-2">
                {NEWSLETTER_MODAL_COPY.success.body}
              </DialogDescription>
            </div>
          ) : (
            <>
              <DialogTitle className="font-serif text-2xl font-semibold tracking-tight">
                {copy.headline}
              </DialogTitle>
              <DialogDescription className="mt-2">{copy.body}</DialogDescription>

              <form onSubmit={handleSubmit} className="mt-6 space-y-3" noValidate>
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <div ref={turnstileRef} />
                <Label htmlFor="newsletter-modal-email" className="sr-only">
                  Email address
                </Label>
                <Input
                  id="newsletter-modal-email"
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError(null)
                  }}
                  required
                  autoFocus
                  autoComplete="email"
                  aria-invalid={emailError ? true : undefined}
                />
                {emailError && (
                  <p className="text-base text-destructive">{emailError}</p>
                )}
                <Button type="submit" disabled={status === 'loading'} className="w-full">
                  {status === 'loading' ? 'Subscribing...' : copy.button}
                </Button>
                <p className="mt-3 text-center text-sm text-muted-foreground">
                  {NEWSLETTER_MODAL_COPY.footnote}
                </p>
              </form>

              {status === 'error' && (
                <p className="mt-3 text-base text-destructive">
                  Something went wrong. Please try again.
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
